import { AxiosError } from 'axios';
import { ApiError } from '../types/api';
import { isAuthSessionExpiredHandledError } from './authSession';
import { toastManager } from './notifications';

export const handleApiError = (error: unknown): string => {
  if (isAuthSessionExpiredHandledError(error)) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: ApiError & { code?: string }; error_code?: string; message?: string } | undefined;
    const apiError = data?.error ?? data;
    const code = apiError?.error_code ?? (apiError as any)?.code ?? (data as any)?.error_code;
    const message = apiError?.message ?? (data as any)?.message;

    if (code === 'TEACHER_NOT_ACTIVE' || (typeof message === 'string' && message.toUpperCase().includes('TEACHER_NOT_ACTIVE'))) {
      return 'Teacher is not active. Please invite and ensure they accept.';
    }
    if (code === 'LIMIT_EXCEEDED' || (typeof message === 'string' && message.toLowerCase().includes('limit reached'))) {
      return 'Limit reached: max primary/secondary classes per teacher.';
    }
    if (error.response?.status === 409 && typeof message === 'string') {
      const m = message.toLowerCase();
      if (m.includes('assign') || m.includes('assigned') || m.includes('delete')) {
        return 'Cannot delete: teacher assigned to a class.';
      }
    }

    // Prefer backend message (e.g. "Current password is incorrect" for update-password 401)
    if (message && typeof message === 'string') {
      return message;
    }

    if (error.response?.status === 401) {
      return 'Authentication required. Please login again.';
    }
    
    if (error.response?.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    
    if (error.response?.status === 404) {
      return 'The requested resource was not found.';
    }
    
    if (error.response?.status === 422) {
      const details = apiError && typeof apiError === 'object' && 'details' in apiError ? (apiError as ApiError).details : undefined;
      if (details) {
        const firstError = Object.values(details)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return firstError[0];
        }
      }
      return 'Validation error. Please check your input.';
    }
    
    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later.';
    }
    
    if (error.message === 'Network Error') {
      return 'Network error. Please check your connection.';
    }
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

export const showErrorToast = (error: unknown) => {
  const message = handleApiError(error);
  toastManager.error(message);
};

export const showSuccessToast = (message: string) => {
  toastManager.success(message);
};

/** Show a success message in the center of the page (e.g. course/writing feedback). */
export const showSuccessToastCenter = (message: string, duration = 2000) => {
  toastManager.successCenter(message, duration);
};

export const showWarningToast = (message: string) => {
  toastManager.warning(message);
};

